
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GroupMember, GroupRole } from "@/lib/types";
import { toast } from "sonner";
import { toDatabaseValue } from "@/utils/supabase/type-guards";

export function useMembersActions(
  groupId: string,
  currentUserRole: GroupRole,
  currentUserId: string,
  members: GroupMember[],
  refreshMembers: () => void
) {
  const [memberIdToPromote, setMemberIdToPromote] = useState<string | null>(null);
  const [memberIdToDemote, setMemberIdToDemote] = useState<string | null>(null);
  const [memberIdToBan, setMemberIdToBan] = useState<string | null>(null);
  const [memberIdToUnban, setMemberIdToUnban] = useState<string | null>(null);
  const [memberIdToMute, setMemberIdToMute] = useState<string | null>(null);
  const [memberIdToUnmute, setMemberIdToUnmute] = useState<string | null>(null);
  const [memberIdToKick, setMemberIdToKick] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);

  const handlePromoteMember = async (memberId: string, currentRole: GroupRole) => {
    // Only admins can promote members
    if (currentUserRole !== "admin") {
      toast.error("Only admins can promote members");
      return;
    }

    const newRole = currentRole === "member" ? "moderator" : "admin";
    
    try {
      // First check limits for the target role
      if (newRole === "admin") {
        const adminCount = members.filter(m => m.role === "admin").length;
        if (adminCount >= 10) {
          toast.error("Maximum admin limit (10) reached for this group");
          return;
        }
      } else if (newRole === "moderator") {
        const modCount = members.filter(m => m.role === "moderator").length;
        if (modCount >= 50) {
          toast.error("Maximum moderator limit (50) reached for this group");
          return;
        }
      }

      const { error } = await supabase
        .from("group_members")
        .update(toDatabaseValue({ role: newRole }))
        .eq("id", toDatabaseValue(memberId));

      if (error) throw error;

      toast.success(`Member promoted to ${newRole}`);
      setMemberIdToPromote(null);
      setConfirmAction(null);
      refreshMembers();
    } catch (error: any) {
      console.error("Error promoting member:", error);
      toast.error(error.message || "Failed to promote member. Please try again.");
    }
  };

  const handleDemoteMember = async (memberId: string, userId: string, currentRole: GroupRole) => {
    // Only admins can demote members
    if (currentUserRole !== "admin") {
      toast.error("Only admins can demote members");
      return;
    }

    // Cannot demote yourself if you're the only admin
    if (userId === currentUserId && currentRole === "admin") {
      const adminCount = members.filter(m => m.role === "admin").length;
      if (adminCount <= 1) {
        toast.error("You cannot demote yourself as you are the only admin");
        return;
      }
    }

    const newRole = currentRole === "admin" ? "moderator" : "member";
    
    try {
      const { error } = await supabase
        .from("group_members")
        .update(toDatabaseValue({ role: newRole }))
        .eq("id", toDatabaseValue(memberId));

      if (error) throw error;

      toast.success(`Member demoted to ${newRole}`);
      setMemberIdToDemote(null);
      setConfirmAction(null);
      refreshMembers();
    } catch (error: any) {
      console.error("Error demoting member:", error);
      toast.error(error.message || "Failed to demote member. Please try again.");
    }
  };

  const handleBanMember = async (memberId: string, userId: string) => {
    // Only admins can ban members
    if (currentUserRole !== "admin") {
      toast.error("Only admins can ban members");
      return;
    }

    // Cannot ban yourself
    if (userId === currentUserId) {
      toast.error("You cannot ban yourself");
      return;
    }

    try {
      const { error } = await supabase
        .from("group_members")
        .update(toDatabaseValue({ is_banned: true }))
        .eq("id", toDatabaseValue(memberId));

      if (error) throw error;

      toast.success("Member banned from the group");
      setMemberIdToBan(null);
      setConfirmAction(null);
      refreshMembers();
    } catch (error: any) {
      console.error("Error banning member:", error);
      toast.error(error.message || "Failed to ban member. Please try again.");
    }
  };

  const handleUnbanMember = async (memberId: string) => {
    // Only admins can unban members
    if (currentUserRole !== "admin") {
      toast.error("Only admins can unban members");
      return;
    }

    try {
      const { error } = await supabase
        .from("group_members")
        .update(toDatabaseValue({ is_banned: false }))
        .eq("id", toDatabaseValue(memberId));

      if (error) throw error;

      toast.success("Member unbanned from the group");
      setMemberIdToUnban(null);
      setConfirmAction(null);
      refreshMembers();
    } catch (error: any) {
      console.error("Error unbanning member:", error);
      toast.error(error.message || "Failed to unban member. Please try again.");
    }
  };

  const handleMuteMember = async (memberId: string, userId: string) => {
    // Admins and moderators can mute members
    if (currentUserRole !== "admin" && currentUserRole !== "moderator") {
      toast.error("Only admins and moderators can mute members");
      return;
    }

    // Cannot mute yourself
    if (userId === currentUserId) {
      toast.error("You cannot mute yourself");
      return;
    }

    // Cannot mute admins (if you're a moderator)
    const memberToMute = members.find(m => m.id === memberId);
    if (currentUserRole === "moderator" && memberToMute?.role === "admin") {
      toast.error("Moderators cannot mute admins");
      return;
    }

    // Set mute for 1 hour
    const mutedUntil = new Date();
    mutedUntil.setHours(mutedUntil.getHours() + 1);
    
    try {
      const { error } = await supabase
        .from("group_members")
        .update(toDatabaseValue({
          is_muted: true,
          muted_until: mutedUntil.toISOString()
        }))
        .eq("id", toDatabaseValue(memberId));

      if (error) throw error;

      toast.success("Member muted for 1 hour");
      setMemberIdToMute(null);
      setConfirmAction(null);
      refreshMembers();
    } catch (error: any) {
      console.error("Error muting member:", error);
      toast.error(error.message || "Failed to mute member. Please try again.");
    }
  };

  const handleUnmuteMember = async (memberId: string) => {
    // Admins and moderators can unmute members
    if (currentUserRole !== "admin" && currentUserRole !== "moderator") {
      toast.error("Only admins and moderators can unmute members");
      return;
    }

    try {
      const { error } = await supabase
        .from("group_members")
        .update(toDatabaseValue({
          is_muted: false,
          muted_until: null
        }))
        .eq("id", toDatabaseValue(memberId));

      if (error) throw error;

      toast.success("Member unmuted");
      setMemberIdToUnmute(null);
      setConfirmAction(null);
      refreshMembers();
    } catch (error: any) {
      console.error("Error unmuting member:", error);
      toast.error(error.message || "Failed to unmute member. Please try again.");
    }
  };

  const handleKickMember = async (memberId: string, userId: string) => {
    // Admins can kick any member, moderators can only kick regular members
    if (currentUserRole !== "admin" && currentUserRole !== "moderator") {
      toast.error("Only admins and moderators can kick members");
      return;
    }

    // Cannot kick yourself
    if (userId === currentUserId) {
      toast.error("You cannot kick yourself");
      return;
    }

    // Moderators cannot kick admins or other moderators
    const memberToKick = members.find(m => m.id === memberId);
    if (currentUserRole === "moderator" && 
        (memberToKick?.role === "admin" || memberToKick?.role === "moderator")) {
      toast.error("Moderators can only kick regular members");
      return;
    }

    try {
      const { error } = await supabase
        .from("group_members")
        .delete()
        .eq("id", toDatabaseValue(memberId));

      if (error) throw error;

      toast.success("Member kicked from the group");
      setMemberIdToKick(null);
      setConfirmAction(null);
      refreshMembers();
    } catch (error: any) {
      console.error("Error kicking member:", error);
      toast.error(error.message || "Failed to kick member. Please try again.");
    }
  };

  return {
    memberIdToPromote,
    setMemberIdToPromote,
    memberIdToDemote,
    setMemberIdToDemote,
    memberIdToBan,
    setMemberIdToBan,
    memberIdToUnban,
    setMemberIdToUnban,
    memberIdToMute,
    setMemberIdToMute,
    memberIdToUnmute,
    setMemberIdToUnmute,
    memberIdToKick,
    setMemberIdToKick,
    confirmAction,
    setConfirmAction,
    handlePromoteMember,
    handleDemoteMember,
    handleBanMember,
    handleUnbanMember,
    handleMuteMember,
    handleUnmuteMember,
    handleKickMember,
  };
}
